import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { salesApi } from '../../services/salesApi.js';
import { formatDate } from '../../utils/formatters.js';
import { quotePdfAttachment } from '../../utils/pdf.js';

/**
 * "An Kunde senden": e-mails the quote PDF via the configured SMTP server
 * (finance /email/send) and only then flips the quote to SENT. A ghost
 * action covers the no-SMTP case by marking as sent without an e-mail.
 * Pass any quote row (`{id, quoteNumber}`) - the detail incl. lines is
 * fetched here so the list page can use the modal too.
 */
export function QuoteSendModal({ quote, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const open = Boolean(quote);
  const id = quote?.id;

  const detail = useQuery({
    queryKey: ['sales', 'quotes', id],
    queryFn: () => salesApi.getQuote(id).then((r) => r.items),
    enabled: open,
  });
  const customers = useQuery({
    queryKey: ['sales', 'customers'],
    queryFn: () => salesApi.listCustomers().then((r) => r.items || []),
    enabled: open,
  });
  const settings = useQuery({
    queryKey: ['finance', 'company-settings'],
    queryFn: () => financeApi.getCompanySettings().then((r) => r.items),
    retry: false,
    enabled: open,
  });

  const customer = (customers.data || []).find((c) => c.id === detail.data?.customerId);

  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    if (!open || !detail.data) return;
    const q = detail.data;
    setTo(customer?.email || '');
    const base = t('quote.email.subject', { number: q.quoteNumber });
    setSubject(settings.data?.companyName ? `${base} - ${settings.data.companyName}` : base);
    setBody(t('quote.email.body', { number: q.quoteNumber, validUntil: formatDate(q.validUntil) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, detail.data?.id, customer?.email, settings.data?.companyName]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['sales', 'quotes'] });

  const sendMail = useMutation({
    mutationFn: async () => {
      // Build from status SENT so the customer copy carries no draft stamp.
      const att = await quotePdfAttachment({
        quote: { ...detail.data, status: 'SENT' },
        customer,
        settings: settings.data,
        t,
      });
      await financeApi.sendEmail({
        to,
        subject,
        text: body,
        attachmentBase64: att.base64,
        attachmentName: att.name,
      });
      return salesApi.sendQuote(id);
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const markOnly = useMutation({
    mutationFn: () => salesApi.sendQuote(id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const busy = sendMail.isPending || markOnly.isPending;
  const error = sendMail.error || markOnly.error;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('quote.email.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button variant="ghost" onClick={() => markOnly.mutate()} disabled={busy}>
            {t('quote.email.markOnly')}
          </Button>
          <Button variant="primary" onClick={() => sendMail.mutate()} disabled={busy || !to || !detail.data}>
            {sendMail.isPending ? `${t('email.send')}…` : t('email.send')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <p className="muted text-sm" style={{ margin: 0 }}>{t('email.intro')}</p>
        <label className="field">
          <span className="field-label">{t('email.to')}</span>
          <input className="input" type="email" value={to} onChange={(e) => setTo(e.target.value)} required />
        </label>
        <label className="field">
          <span className="field-label">{t('email.subject')}</span>
          <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} required />
        </label>
        <label className="field">
          <span className="field-label">{t('email.message')}</span>
          <textarea className="textarea" rows={7} value={body} onChange={(e) => setBody(e.target.value)} />
        </label>
        {error && <div className="alert danger">{error?.message || t('common.loadError')}</div>}
      </div>
    </Modal>
  );
}
